import { Suspense, lazy, useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ReactFlowProvider } from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import { AlertTriangle, ArrowLeft, Circle, ShieldAlert } from 'lucide-react'
import { AppStateProvider } from '@/features/visualization/_legacy/context/AppStateContext'
import { setImpactMarks, clearImpactMarks } from '@/features/visualization/_legacy/utils/impactMarks'
import { useCan } from '@/hooks/useCan'
import { assessmentKey, impactApi, useImpactAssessments, useImpactOutcome } from '../impact-assessment'
import { ImpactOverview } from '../components/impact/ImpactOverview'

const VisualizationApp = lazy(() => import('@/features/visualization/_legacy/App'))

/**
 * Impact assessment on the canvas, for one impact assessment (its own record since 14 Oct 2026).
 *
 * Andrew, 13 Oct 2026: "impact assessment could have the hierarchy displayed on the canvas. We
 * could have options to tag objects in the hierarchy that have been compromised. The canvas would
 * need to show multiple recovery scenarios". The Impact Assessment tab in recovery manager keeps
 * the questions and the answer; this is the same assessment drawn on the hierarchy, where an
 * engineer marks an object by clicking the mark on its node and sees at once what sits above it.
 *
 * It is the planning canvas, read-only as to structure (nothing can be linked or moved here),
 * with the assessment's marks laid over it (utils/impactMarks.js). The hierarchy drawn is the
 * production scenario's - what is "affected" is always measured against production (14 Oct 2026);
 * the other scenarios are ways back, listed on each affected service. The marks themselves belong
 * to the assessment, not to a scenario.
 *
 * Down the left, narrow: the locations, platforms and applications to click as compromised.
 * Down the right: the business services impacted in this scenario; clicking one draws its
 * hierarchy. Each side folds away to a strip, as the canvas's own list of main nodes does
 * (Andrew, 13 Oct 2026). The canvas's control panel is shown - node modes, edges, spacing,
 * group by, layers - because none of it edits the hierarchy.
 */
function Side({ side, open, onToggle, label, children }: { side: 'left' | 'right'; open: boolean; onToggle: () => void; label: string; children: React.ReactNode }) {
    // The same handle as the canvas's own list of main nodes: a small tab with "<" or ">".
    const points = (side === 'left') === open ? '<' : '>'
    const handle = (
        <div className="flex w-5 flex-shrink-0 items-center border-x bg-background">
            <button type="button" onClick={onToggle} aria-expanded={open} aria-label={label} title={label} className="h-10 w-full rounded bg-muted font-bold leading-none text-muted-foreground hover:text-foreground">{points}</button>
        </div>
    )
    return (
        // The same edge as the planning canvas's main nodes panel (App.css .main-palette): a 2px
        // border and a soft shadow cast onto the canvas, mirrored for the right-hand panel.
        <aside
            className="relative z-10 flex flex-shrink-0 bg-background"
            style={side === 'left'
                ? { borderRight: '2px solid hsl(var(--border))', boxShadow: '4px 0 6px -2px rgba(0, 0, 0, 0.3)' }
                : { borderLeft: '2px solid hsl(var(--border))', boxShadow: '-4px 0 6px -2px rgba(0, 0, 0, 0.3)' }}
            aria-label={label}
            data-side={side}
            data-open={open}
        >
            {side === 'right' && handle}
            {open && <div className={side === 'right' ? 'w-64 min-w-0' : 'w-56 min-w-0'}>{children}</div>}
            {side === 'left' && handle}
        </aside>
    )
}


export function ImpactAssessmentCanvasPage() {
    const { t } = useTranslation()
    const { assessmentId: idParam } = useParams<{ assessmentId: string }>()
    const assessmentId = Number.parseInt(idParam || '', 10)
    const valid = Number.isInteger(assessmentId) && assessmentId > 0
    const queryClient = useQueryClient()
    const canMark = useCan('recover-requests.update')
    const { data: assessments } = useImpactAssessments()
    const assessment = assessments?.find((a) => a.id === assessmentId)
    // The hierarchy drawn is production's: what the assessment is measured against (Andrew, 14 Oct
    // 2026: "which ever is marked as production is what the impact assessment and the impact
    // assessment canvas use"). There is no scenario to choose here; each affected service on the
    // right lists how every scenario would bring it back.
    const { data: outcome, isLoading: loadingOutcome } = useImpactOutcome(valid ? assessmentId : null)
    const scenario = outcome?.production?.id ?? null
    const [mainNodeId, setMainNodeId] = useState<string | undefined>(undefined)
    const [leftOpen, setLeftOpen] = useState(true)
    const [rightOpen, setRightOpen] = useState(true)

    // The canvas fills the window below the top bar (the same sizing as the planning canvas page).
    useEffect(() => {
        const main = document.querySelector('main') as HTMLElement | null
        const container = main?.querySelector(':scope > div') as HTMLElement | null
        if (main) { main.style.overflow = 'hidden'; main.style.padding = '0' }
        if (container) { container.style.padding = '0'; container.style.maxWidth = 'none'; container.style.height = '100%' }
        return () => {
            if (main) { main.style.overflow = ''; main.style.padding = '' }
            if (container) { container.style.padding = ''; container.style.maxWidth = ''; container.style.height = '' }
        }
    }, [])

    const marksKey = useMemo(() => [...assessmentKey(assessmentId), 'canvas-marks', scenario], [assessmentId, scenario])
    const { data: marks } = useQuery({
        queryKey: marksKey,
        queryFn: async () => (await impactApi.marks(assessmentId, scenario as number)).data.data,
        enabled: valid && scenario != null,
    })

    const toggle = useCallback(async (objectId: string, nowCompromised: boolean) => {
        if (!marks?.markable?.includes(objectId)) return
        await impactApi.mark(assessmentId, { locationIds: [], classificationIds: [], technologyIds: [], objectIds: [objectId], objectStatus: nowCompromised ? 2 : 4 })
        // The page's lists, the questions' counts and the outcome all read the same marks.
        queryClient.invalidateQueries({ queryKey: assessmentKey(assessmentId) })
        queryClient.invalidateQueries({ queryKey: ['impact-assessments', 'list'] })
    }, [marks, assessmentId, queryClient])

    // Lay the marks over the canvas while this page is open; take them away when it closes.
    useEffect(() => {
        if (!marks) return
        setImpactMarks({ compromised: marks.compromised, affected: marks.affected, canMark, onToggle: toggle })
    }, [marks, canMark, toggle])
    useEffect(() => () => clearImpactMarks(), [])

    if (!valid) return <p className="p-6 text-sm text-muted-foreground">{t('recoverRequests.impact.noAssessment', 'This impact assessment was not found.')}</p>

    const legend = 'inline-flex items-center gap-1 text-xs text-muted-foreground'
    return (
        <div style={{ width: '100%', height: 'calc(100vh - var(--topbar-height, 3.5rem))', overflow: 'hidden' }} className="flex flex-col">
            <header className="flex flex-shrink-0 flex-wrap items-center gap-x-4 gap-y-1 border-b bg-background px-4 py-2">
                <Link to={`/impact-assessment?assessment=${assessmentId}`} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
                    <ArrowLeft className="h-4 w-4" />
                    {t('recoverRequests.impact.backToAssessment', 'Back to the assessment')}
                </Link>
                <h1 className="text-sm font-semibold">
                    {t('recoverRequests.mobilisation.impactAssessment', 'Impact Assessment')}
                    {assessment?.heading ? ` — ${assessment.heading}` : ''}
                </h1>
                {outcome?.production && (
                    <span className="text-xs" data-testid="impact-canvas-production">
                        {t('recoverRequests.impact.canvas.production', 'Production hierarchy: {{name}}', { name: outcome.production.name })}
                    </span>
                )}
                <span className="text-xs text-muted-foreground tabular-nums" data-testid="impact-canvas-count">
                    {t('recoverRequests.impact.canvasCountProduction', '{{compromised}} compromised · {{affected}} affected in production', { compromised: marks?.compromised.length ?? 0, affected: marks?.affected.length ?? 0 })}
                </span>
                <span className="ml-auto flex flex-wrap items-center gap-3">
                    <span className={legend}><ShieldAlert className="h-3.5 w-3.5 text-red-600" />{t('recoverRequests.impact.markCompromised', 'Compromised')}</span>
                    <span className={legend}><AlertTriangle className="h-3.5 w-3.5 text-amber-600" />{t('recoverRequests.impact.legendAffected', 'Affected')}</span>
                    {canMark && <span className={legend}><Circle className="h-3.5 w-3.5 text-gray-400" />{t('recoverRequests.impact.legendClick', 'Click a mark to change it')}</span>}
                </span>
            </header>
            <div className="flex min-h-0 flex-1">
                <Side side="left" open={leftOpen} onToggle={() => setLeftOpen((v) => !v)} label={t('recoverRequests.impact.canvas.compromisedPanel', 'What is compromised')}>
                    <ImpactOverview assessmentId={assessmentId} variant="selectors" />
                </Side>
                <div className="min-h-0 min-w-0 flex-1">
                {scenario == null ? (
                    !loadingOutcome && outcome && (
                        <p className="m-6 rounded-md border border-amber-500/60 bg-amber-500/10 p-3 text-sm text-amber-800 dark:text-amber-300" data-testid="impact-canvas-no-production">
                            {t('recoverRequests.impact.production.none', 'No recovery scenario is marked production, so nothing can be measured. Mark the scenario that is how the minimum viable company runs today on the Recovery Scenarios screen.')}
                        </p>
                    )
                ) : (
                    <Suspense fallback={<div className="flex h-full items-center justify-center text-sm text-muted-foreground">{t('common.loading', 'Loading...')}</div>}>
                        <AppStateProvider>
                            <ReactFlowProvider>
                                <VisualizationApp
                                    scenario="planning"
                                    view="dependency"
                                    objectId={mainNodeId}
                                    requestId={undefined}
                                    showMainPalette
                                    // The hierarchy is not edited here: only the assessment's marks change.
                                    readOnly
                                    showControls
                                    scenarioId={String(scenario)}
                                    serviceId={String(scenario)}
                                    onMainNodeChange={setMainNodeId as unknown as (id: unknown) => void}
                                />
                            </ReactFlowProvider>
                        </AppStateProvider>
                    </Suspense>
                )}
                </div>
                <Side side="right" open={rightOpen} onToggle={() => setRightOpen((v) => !v)} label={t('recoverRequests.impact.overview.services', 'Business services')}>
                    <ImpactOverview assessmentId={assessmentId} variant="services" onServiceClick={setMainNodeId} />
                </Side>
            </div>
        </div>
    )
}
